import { useEffect, useState } from "react";
const DEFAULT_DELAY = 500;
/**
 * A custom hook that debounces a value
 * @param value - The value to debounce
 * @param delay - The delay in milliseconds (optional, defaults to 300ms)
 * @returns The debounced value
 */
export function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const actualDelay = delay ?? DEFAULT_DELAY;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, actualDelay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, actualDelay]);

  return debouncedValue;
}

/**
 * A custom hook that provides a debounced callback function
 * @param callback - The function to debounce
 * @param delay - The delay in milliseconds (optional, defaults to 300ms)
 * @returns A debounced version of the callback
 */
export function useDebouncedCallback<T extends (...args: unknown[]) => unknown>(
  callback: T,
  delay?: number
): T {
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  const debouncedCallback = ((...args: Parameters<T>) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const actualDelay = delay ?? DEFAULT_DELAY;
    const newTimer = setTimeout(() => {
      callback(...args);
    }, actualDelay);

    setDebounceTimer(newTimer);
  }) as T;

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  return debouncedCallback;
}
